import { call, fork, put, race, take } from 'redux-saga/effects';
import { takeEvery, eventChannel } from 'redux-saga';
import axios from 'axios';
import { errorAction } from 'components/utility/actions';
import FlowsClient from 'rest/FlowsClient';
import FlowDebuggerClient from 'websockets/FlowDebuggerClient';
import NodehandlersClient from 'rest/NodehandlersClient';
import TriggersClient from 'rest/TriggersClient';
import EndpointsClient from 'rest/EndpointsClient';
import browserHistory from 'components/utility/history';
import * as actions from './actions';
import * as restartActions from '../../widgets/Restart/actions';
// import { isLocalStorageAvailable } from '../../utility/utils';

export const flowsClient = new FlowsClient();
const handlersClient = new NodehandlersClient();
const triggersClient = new TriggersClient();
const endpointsClient = new EndpointsClient();

// this function creates an event channel from a given socket
// Setup subscription to incoming events
export function createDebuggerChannel(flowsDebuggerClient) {
	// `eventChannel` takes a subscriber function
	// the subscriber function takes an `emit` argument to put messages onto the channel
	const ev = eventChannel(emit => {
		// setup the subscriptions
		flowsDebuggerClient.onRespond(response => {
			emit(actions.debugFlowRespond(response));
		});
		flowsDebuggerClient.onError(error => {
			emit(actions.debugFlowError(error));
		});
		flowsDebuggerClient.onClose(() => {
			emit(actions.debugFlowStop());
		});

		// the subscriber must return an unsubscribe function
		// this will be invoked when the saga calls `channel.close` method
		return () => {
			// remove the subscriptions
			flowsDebuggerClient.unregisterEventListeners();
		};
	});
	return ev;
}

export function doConnectFlowDebug(flowsDebuggerClient) {
	return flowsDebuggerClient.connect();
}

export async function doStartFlowDebug(action, flowsDebuggerClient) {
	const { flow, params, options } = action.payload;
	return flowsDebuggerClient.start(flow, params, options);
}

export function doFetchFlow(action) {
	return flowsClient.getFlow(action.payload.flowId);
}

export function doFetchFlowTriggers(action) {
	return flowsClient.getFlowTriggers(action.payload.flowId);
}

export function doFetchFlowNodeHandlers() {
	return handlersClient.getSpecs();
}

export function doFetchFlowTriggerDefinitions() {
	return triggersClient.getDefinitions();
}

let cancelValidatingFlow;
export function doValidateFlow(action) {
	return new Promise((resolve, reject) => {
		// cancel any validation in progress
		if (cancelValidatingFlow) {
			cancelValidatingFlow();
			cancelValidatingFlow = null;
		}
		flowsClient.validateFlow(action.payload.flow, (cancel) => {
			cancelValidatingFlow = cancel;
		}).then((response) => {
			cancelValidatingFlow = null;
			resolve(response);
		}).catch((err) => {
			cancelValidatingFlow = null;
			reject(err);
		});
	});
}

export function doSaveFlow(action) {
	const { flowId, flow } = action.payload;
	return flowsClient.saveFlow(flowId, flow);
}

export function doBindFlow(action) {
	const data = {
		method: action.payload.method,
		path: action.payload.path,
		flow: action.payload.flow
	};
	return endpointsClient.bindFlow(action.payload.endpointId, data);
}

export function doCreateFlow(action) {
	return flowsClient.createFlow(action.payload.flow);
}

export function *startDebugFlow(action) {
	try {
		const flowsDebuggerClient = new FlowDebuggerClient();
		// bind events
		const debugChannel = yield call(createDebuggerChannel, flowsDebuggerClient);
		// connect to the debugger
		yield call(doConnectFlowDebug, flowsDebuggerClient);
		// start debugging
		yield call(doStartFlowDebug, action, flowsDebuggerClient);
		while (true) {
			// An error from debugChannel will cause the saga jump to the catch block
			// "take" the action from the event that was emitted
			const action = yield take(debugChannel);
			// now "put" to redux
			yield put(action);
		}
	} catch (e) {
		const err = actions.debugFlowError(e);
		yield put(err);
	}
}

export function *fetchFlowNodeHandlers(action) {
	try {
		const response = yield call(doFetchFlowNodeHandlers, action);
		yield put({
			payload: response.data,
			type: actions.FETCH_FLOW_NODE_HANDLERS_SUCCESS
		});
	} catch (error) {
		const err = errorAction(actions.FETCH_FLOW_NODE_HANDLERS_ERROR, error, action);
		yield put(err);
	}
}

export function *fetchFlowTriggerDefinitions(action) {
	try {
		const response = yield call(doFetchFlowTriggerDefinitions, action);
		yield put({
			payload: response.data,
			type: actions.FETCH_FLOW_TRIGGER_DEFINITIONS_SUCCESS
		});
	} catch (error) {
		const err = errorAction(
			actions.FETCH_FLOW_TRIGGER_DEFINITIONS_ERROR, error, action);
		yield put(err);
	}
}

export function *fetchFlow(action) {
	try {
		// Runs the two fetches for the flow and flow-triggers in parallel
		const [ flow, flowTriggers ] = yield [
			call(doFetchFlow, action),
			call(doFetchFlowTriggers, action)
		];

		yield put({
			payload: {
				flowId: action.payload.flowId,
				flow: flow.data,
				flowTriggers: flowTriggers.data
			},
			type: actions.FETCH_FLOW_SUCCESS
		});

		yield put({
			payload: {
				flow: flow.data
			},
			type: actions.VALIDATE_FLOW
		});
	} catch (error) {
		const err = errorAction(actions.FETCH_FLOW_ERROR, error, action);
		err.payload.flowId = action.payload.flowId;
		yield put(err);
	}
}

export function *validateFlow(action) {
	try {
		const response = yield call(doValidateFlow, action);
		yield put({
			payload: {
				...response.data
			},
			type: actions.VALIDATE_FLOW_SUCCESS
		});
	} catch (error) {
		if (axios.isCancel(error)) {
			yield put({ type: actions.VALIDATE_FLOW_CANCEL });
		} else {
			const err = errorAction(actions.VALIDATE_FLOW_ERROR, error, action);
			yield put(err);
		}
	}
}

export function *saveFlow(action) {
	try {
		yield call(doSaveFlow, action);
		// puts the same flowId, flow back in the response and notify that save was
		// successful.  it will not update the x-validation (no point) - the
		// x-validation is added by APIBuilder when loading the flow.  saving a flow will
		// trigger a reload, and then update x-validation as appropriate.
		yield put({ type: actions.SAVE_FLOW_SUCCESS, payload: action.payload });
	} catch (error) {
		const err = errorAction(actions.SAVE_FLOW_ERROR, error, action);
		err.payload.flowId = action.payload.flowId;
		yield put(err);
		return;
	}

	const [ result ] = yield [
		race({
			success: take(restartActions.AWAIT_RESTART_SUCCESS),
			error: take(restartActions.AWAIT_RESTART_ERROR),
			timeout: take(restartActions.AWAIT_RESTART_TIMEOUT)
		}),
		put(restartActions.awaitRestart())
	];

	if (result && result.success) {
		yield put(actions.clearFlowValidation());
		const [ fetchResult ] = yield [
			race({
				success: take(actions.FETCH_FLOW_SUCCESS),
				error: take(actions.FETCH_FLOW_ERROR)
			}),
			put({ type: actions.FETCH_FLOW, payload: action.payload })
		];
		if (fetchResult && fetchResult.success) {
			yield put({ type: actions.SAVE_FLOW_COMPLETED, payload: action.payload });
		} else {
			yield put({ type: actions.SAVE_FLOW_ERROR });
		}
	}

	const { redirect } = action.payload;
	if (redirect) {
		// successfully saved, exit
		browserHistory.push(redirect);
	}
}

export function *createFlow(action) {
	let response;
	const flowType = action.payload.flowType;
	try {
		if (flowType === 'endpoint') {
			response = yield call(doBindFlow, action);
		} else {
			response = yield call(doCreateFlow, action);
		}
		// puts the same flowId, flow back in the response and notify that save was
		// successful.  it will not update the x-validation (no point) - the
		// x-validation is added by APIBuilder when loading the flow.  saving a flow will
		// trigger a reload, and then update x-validation as appropriate.
		yield put({
			type: actions.SAVE_FLOW_SUCCESS,
			payload: {
				flowId: action.payload.flowId
			}
		});
	} catch (error) {
		const err = errorAction(actions.SAVE_FLOW_ERROR, error, action);
		err.payload.flowId = action.payload.flowId;
		yield put(err);
		return;
	}
	const [ result ] = yield [
		race({
			success: take(restartActions.AWAIT_RESTART_SUCCESS),
			error: take(restartActions.AWAIT_RESTART_ERROR),
			timeout: take(restartActions.AWAIT_RESTART_TIMEOUT)
		}),
		put(restartActions.awaitRestart())
	];
	if (result && result.success) {
		const flowId = response.headers && response.headers.location;
		const decodedId = decodeURIComponent(flowId);
		yield put(actions.clearFlowValidation());
		yield put({
			type: actions.SAVE_FLOW_COMPLETED,
			payload: {
				...action.payload,
				flowId: decodedId
			}
		});

		// We intentionally switch off local storage and we will turn it on
		// with: https://jira.axway.com/browse/RDPP-6574
		// if (isLocalStorageAvailable()) {
		// 	// Move the flow debug state under the created flowId, preserving
		// 	// the parameters that might have been used from "new_flow" to the
		// 	// new "decodedId".
		// 	const projectName = action.payload.projectName;
		// 	const state = localStorage
		// 		.getItem(`flow-debug-${projectName}-$$_new_flow`);
		// 	localStorage
		// 		.setItem(`flow-debug-${projectName}-${decodedId}`, state);
		// }

		const { redirect } = action.payload;
		if (redirect) {
			// Redirect to the previous location
			browserHistory.push(redirect);
		} else { // on applyChanges
			browserHistory.replace(
				`${window.$conf.prefix}/project/flows/${flowId}/edit`);
		}
	}
}

export function *deleteFlow(action) {
	const { flowId, redirect } = action.payload;

	try {
		yield call([ flowsClient, flowsClient.deleteFlow ], flowId);
	} catch (error) {
		const err = errorAction(actions.DELETE_FLOW_ERROR, error, action);
		err.payload.flowId = flowId;
		yield put(err);
		return;
	}

	// Start waiting for the restart success and show the restart modal.
	yield put(restartActions.awaitRestart());
	// Don't worry about AWAIT_RESTART failures here. These actions are handled elsewhere and will
	// update the restart modal. We will only continue this action if the restart "flow" succeeds.
	yield take(restartActions.AWAIT_RESTART_SUCCESS);

	browserHistory.push(redirect);

	// Finish the saga and let the reducers clean up.
	// This is at the end to avoid the flow view needing to handle
	// no flow in the store that matches the id in the path.
	yield put(actions.clearFlowValidation());
	yield put({
		type: actions.DELETE_FLOW_SUCCESS,
		payload: {
			flowId
		}
	});
}

export default function *sagas() {
	yield fork(takeEvery, actions.FETCH_FLOW, fetchFlow);
	yield fork(takeEvery, actions.FETCH_FLOW_NODE_HANDLERS, fetchFlowNodeHandlers);
	yield fork(takeEvery, actions.FETCH_FLOW_TRIGGER_DEFINITIONS, fetchFlowTriggerDefinitions);
	yield fork(takeEvery, actions.VALIDATE_FLOW, validateFlow);
	yield fork(takeEvery, actions.SAVE_FLOW, saveFlow);
	yield fork(takeEvery, actions.DELETE_FLOW, deleteFlow);
	yield fork(takeEvery, actions.CREATE_ENDPOINT_FLOW, createFlow);
	yield fork(takeEvery, actions.CREATE_UNBOUND_FLOW, createFlow);
	yield fork(takeEvery, actions.DEBUG_FLOW_START, startDebugFlow);
}
